// src/pages/ProjectDetail.jsx
import { useEffect, useState, useMemo } from "react";
import Sidebar from "../../components/Sidebar";
import Nav from "../../components/Nav";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import "../../styles/ProjectDetail.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

import Axios from "../../utils/Axios";
import { formatCurrency } from "../../utils/format";
import Toast from "../../utils/Toast";

import General from "./General/Index";
import Budget from "./Budgets";
import Task from "./Task";
import AuthHandler from "../../utils/AuthHandler";
import EditProject from "./EditProject";
import { useDispatch, useSelector } from "react-redux";
import { PROJECT_DETAILS_REQUEST } from "../../store/Actions/Actions";
import { DELETE_PROJECT } from "../../config/API_Collection";
import AccessRestricted from "../../components/AccessRestricted";
import SummaryCards from "./SummaryCards";
import MetaBadges from "./MetaBadges";
import InvoiceTab from "./Invoice";

// Reusable loader layout (keeps sidebar/nav/footer to avoid layout jump)
function FullPageLoaderLayout() {
  return (
    <div
      className="project-detail-layout"
      style={{ display: "flex", height: "100vh" }}
    >
      <Sidebar />
      <div
        className="project-detail-content"
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Nav />
        <div className="top-loading-bar" />
        <div className="project-detail-container">
          <div className="loading-wrapper py-5">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Small inline loader for tab bodies
function TabLoader() {
  return (
    <div className="loading-wrapper py-5">
      <div className="spinner-border text-secondary" role="status" />
    </div>
  );
}

export default function ProjectDetail() {
  const navigate = useNavigate();
  const { id: pid } = useParams();
  const dispatch = useDispatch();

  // ---------- Redux state (hooks at top, always called) ----------
  const isLoading = useSelector((s) => s?.projectDetails?.isLoading);
  const projectData = useSelector((s) => s?.projectDetails?.project);

  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null; // null while booting
  const isBootstrapping = !!loginState?.isBootstrapping;
  const [checkUp, setCheckUp] = useState(false);
  // ----- Permissions (booleans) -----
  const canViewProject = actions?.projects?.project_detail === true;
  const canEditProject = actions?.projects?.edit_project === true;
  const canDeleteProject = actions?.projects?.delete_project === true;

  console.log("can edit project==>", canEditProject);

  // Budget permissions
  const canListBudget = actions?.budgets?.budget_list === true;
  const canEditBudget = actions?.budgets?.edit_budget === true;
  const canDeleteBudget = actions?.budgets?.delete_budget === true;
  const canSeeStatus = actions?.budgets?.budget_status === true;

  // Invoice permissions
  const canListInvoices = actions?.invoices?.invoice_list === true;

  // ---------- Local UI state (hooks at top, always called) ----------
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "general";
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [update, setUpdate] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });

  // Update selected tab when URL query changes
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && tabFromUrl !== selectedTab) {
      setSelectedTab(tabFromUrl);
    }
  }, [searchParams]);

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalRemove, setShowModalRemove] = useState(false);

  const showToast = (message, type) => setToast({ message, type });

  useEffect(() => {
    if (pid && canViewProject) {
      dispatch({ type: PROJECT_DETAILS_REQUEST, payload: pid });
    }
  }, [dispatch, pid, canViewProject, update, checkUp]);

  const visibleTabs = useMemo(() => {
    const tabs = ["general", "tasks"];
    if (canListBudget) tabs.splice(1, 0, "budget");
    if (canListInvoices) tabs.push("invoices");
    return tabs;
  }, [canListBudget, canListInvoices]);

  useEffect(() => {
    if (!visibleTabs.includes(selectedTab)) {
      setSelectedTab(visibleTabs[0]);
    }
  }, [visibleTabs, selectedTab]);

  const getActiveLinkClass = (tab) =>
    selectedTab === tab ? "tab-button active-tab" : "tab-button";

  const handleOpenModalEdit = () => setShowModalEdit(true);

  const handleOpenModalRemove = () => setShowModalRemove(true);
  const handleCloseModalRemove = () => setShowModalRemove(false);

  const handleRemoveProject = async () => {
    try {
      const payload = { project_id: pid };
      const response = await Axios.post(DELETE_PROJECT, payload);
      if (response.status === 200) {
        showToast("Project Deleted Successfully!", "success");
        setTimeout(() => navigate("/projects"), 2000);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    } finally {
      handleCloseModalRemove();
    }
  };

  if (isBootstrapping) {
    return <FullPageLoaderLayout />;
  }
  if (!canViewProject) {
    return (
      <div className="h-100" style={{ display: "flex", height: "100vh" }}>
        <Sidebar />
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Nav />
          <div className="h-100">
            <AccessRestricted homePath="client" />
          </div>
        </div>
      </div>
    );
  }

  const refreshProjectData = () => {
    dispatch({ type: PROJECT_DETAILS_REQUEST, payload: pid });
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case "general":
        return <General data={projectData} />;

      case "budget":
        return (
          <Budget
            data={projectData}
            refreshProjectData={refreshProjectData}
            perms={{
              edit_budget: Boolean(canEditBudget),
              delete_budget: Boolean(canDeleteBudget),
              budget_status: Boolean(canSeeStatus),
            }}
          />
        );

      case "tasks":
        return (
          <Task
            data={projectData}
            update={update}
            setUpdate={setUpdate}
            refreshProjectData={refreshProjectData}
          />
        );

      case "invoices":
        return (
          <InvoiceTab
            projectData={projectData}
            project={projectData?.project_data}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="project-detail-layout">
      <Sidebar />
      <div className="project-detail-content">
        <Nav />
        {isLoading && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} />

        <div className="project-detail-container">
          {/* Header */}
          <div className="row mb-3">
            <div className="col">
              <h4 className="project-title">
                <i
                  className={`bi bi-circle-fill fs-6 ${
                    projectData?.project_data?.status === 1
                      ? "text-success"
                      : "text-secondary"
                  }`}
                />
                &nbsp; {projectData?.project_data?.project_code} -{" "}
                {projectData?.project_data?.project_name}
              </h4>
            </div>
            <div className="col-md-2 d-flex justify-content-end me-3">
              {(canEditProject || canDeleteProject) && (
                <div className="dropdown">
                  <button
                    type="button"
                    className="btn btn-sm"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </button>
                  <ul className="dropdown-menu">
                    {canEditProject && (
                      <li onClick={handleOpenModalEdit}>
                        <a className="dropdown-item" href="#">
                          Edit Project
                        </a>
                      </li>
                    )}
                    {canDeleteProject && (
                      <li onClick={handleOpenModalRemove}>
                        <a className="dropdown-item" href="#">
                          Delete Project
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <MetaBadges projectData={projectData} />

          <SummaryCards summaryData={projectData?.stat} />

          {/* Tabs */}
          <div className="tab-nav-container">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={getActiveLinkClass(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="content-section">
            {isLoading ? <TabLoader /> : renderTabContent()}
          </div>
        </div>

        {/* Edit Project Modal */}
        {showModalEdit && canEditProject && (
          <div className="custom-project-modal">
            <div className="modal-backdrop fade show" />
            <div
              className="modal fade show"
              tabIndex="-1"
              aria-labelledby="editProjectLabel"
              aria-hidden="true"
              style={{ display: "block", zIndex: 1050 }}
            >
              <div
                className="modal-dialog modal-lg mx-auto"
                style={{ marginTop: "2%" }}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="editProjectLabel">
                      Edit Project
                    </h5>
                  </div>
                  <div className="modal-body">
                    <EditProject
                      closeModalEdit={() => setShowModalEdit(false)}
                      showToast={showToast}
                      projectData={projectData?.project_data}
                      update={{ checkUp, setCheckUp }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Project Modal */}
        {showModalRemove && canDeleteProject && (
          <div className="custom-project-modal">
            <div className="modal-backdrop fade show" />
            <div
              className="modal fade show"
              tabIndex="-1"
              aria-hidden="true"
              style={{ display: "block", zIndex: 1050 }}
            >
              <div
                className="modal-dialog mx-auto"
                style={{ marginTop: "15%" }}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Project Deletion</h5>
                  </div>
                  <div className="modal-body text-center">
                    <p>Are you sure you want to delete this project?</p>
                  </div>
                  <div className="d-flex justify-content-center gap-5">
                    <button
                      className="btn btn-danger px-5"
                      onClick={handleRemoveProject}
                    >
                      Yes
                    </button>
                    <button
                      className="btn btn-secondary px-5"
                      onClick={() => setShowModalRemove(false)}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
